import { Button, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useToggle } from '@hooks/useToggle';
import { InitiativePlain } from '@g17eco/types/initiative';
import config from '../../../../config';
import { useIntegrateCompanyTrackerMutation } from '@api/initiatives';
import { generateErrorToast } from '@g17eco/molecules/toasts';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { SettingStorage } from '@services/SettingStorage';
import { companyTrackerRouteMatcher } from '@routes/appRootMatcher';
import { useAppDispatch } from '@reducers/index';
import { reloadInitiativeTree } from '@actions/index';
import { FeatureIntro } from '@g17eco/molecules/feature-intro';
import { IntegrationCardVariant } from './types';
import { IntegrationCard } from './IntegrationCard';
import { useState } from 'react';
import { getCurrencyList } from '@utils/units';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SURVEY } from '@constants/terminology';
import { Checkbox, CheckboxState } from '@g17eco/atoms/checkbox';

interface Props {
  initiative: Pick<InitiativePlain, '_id' | 'name'>;
  variant?: IntegrationCardVariant;
}

enum IntegrationModalView {
  Info,
  Currency,
}

const InfoView = ({
  initiative,
  handleBack,
  handleNext,
  isLoading,
}: {
  initiative: Pick<InitiativePlain, 'name'>;
  handleBack: () => void;
  handleNext: () => void;
  isLoading: boolean;
}) => {
  return (
    <>
      <ModalBody>
        {isLoading ? <BlockingLoader /> : null}
        <p>
          Unlock the full potential of your sustainability journey by connecting Company Tracker to {initiative.name}:
        </p>
        <p>Free trial period = 14 days</p>
        <FeatureIntro
          icon='fa-circle-nodes'
          header='Seamless integration across G17Eco'
          content='Effortlessly connect your G17Eco apps with a single click, streamlining everything from completing materiality assessments and gathering data for sustainability reporting to securing third-party assurance.'
        />
        <FeatureIntro
          icon='fa-code-fork'
          header='Workflow Automation'
          content='Streamline collaboration and data collection across complex organisations, and automate validation and tracking.'
        />
        <FeatureIntro
          icon='fa-chart-mixed'
          header='Analytics, insights and sustainability report generation'
          content='Design insightful digital dashboards to enable real-time monitoring of analytics, seamless sharing with internal and external stakeholders, and effortless generation of sustainability reports.'
        />
        <p>
          <a href={config.brochureURL.companyTracker} target='_blank' rel='noreferrer'>
            Click here
          </a>{' '}
          to learn more about Company Tracker.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' onClick={handleBack} className='me-3'>
          Cancel
        </Button>
        <Button color='primary' onClick={handleNext}>
          Continue
        </Button>
      </ModalFooter>
    </>
  );
};

const CurrencyView = ({
  initiative,
  handleBack,
  handleSubmit,
  currency,
  setCurrency,
  confirmed,
  setConfirmed,
}: {
  initiative: Pick<InitiativePlain, 'name'>;
  handleBack: () => void;
  handleSubmit: (currency: string | undefined) => Promise<void>;
  currency: string | undefined;
  setCurrency: React.Dispatch<React.SetStateAction<string | undefined>>;
  confirmed: boolean;
  setConfirmed: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const currencyOptions = getCurrencyList().map((item) => ({
    value: item.abbr,
    label: `${item.singular} (${item.abbr})`,
  }));

  const isDisabled = !currency || !confirmed;

  return (
    <>
      <ModalBody>
        <h6 className='text-ThemeHeadingDark'>Preferred reporting currency</h6>
        <p>
          Company Tracker currently supports one currency per organisation. Please select your preferred currency for
          your {SURVEY.PLURAL} carefully, as this choice is final and can not be updated once you beginning your first
          report.
        </p>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder={'Select preferred currency'}
          options={currencyOptions}
          value={currencyOptions.find((c) => c.value === currency) ?? null}
          onChange={(option) => setCurrency(option?.value)}
        />
        <FormGroup check inline className='mt-2'>
          <Checkbox
            status={confirmed ? CheckboxState.Checked : CheckboxState.Unchecked}
            id='confirm-checkbox'
            onChange={() => setConfirmed(!confirmed)}
          />
          <Label check for='confirm-checkbox'>
            I confirm on behalf of {initiative.name} that we will report in {currency ?? '-'}.
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' onClick={handleBack} className='me-3'>
          <i className='fal fa-caret-left mr-2' /> Back
        </Button>
        <Button color='primary' onClick={() => handleSubmit(currency)} disabled={isDisabled}>
          Start free trial
        </Button>
      </ModalFooter>
    </>
  );
};

export const CompanyTrackerIntegrationCard = ({ initiative, variant = IntegrationCardVariant.Column }: Props) => {
  const [isOpen, toggle] = useToggle(false);
  const [view, setView] = useState<IntegrationModalView>(IntegrationModalView.Info);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [confirmed, setConfirmed] = useState(false);
  const [integrateCT, { isLoading }] = useIntegrateCompanyTrackerMutation();
  const dispatch = useAppDispatch();

  const handleSubmit = async (currency: string | undefined) => {
    try {
      if (!currency) {
        return;
      }
      await integrateCT({ initiativeId: initiative._id, unitConfig: { currency } }).unwrap();

      // Reload root organization to have new appConfigCode
      dispatch(reloadInitiativeTree());
      // Avoid redirect to preselected CT
      SettingStorage.setItem(companyTrackerRouteMatcher.storageKey, initiative._id);
      window.open(generateUrl(ROUTES.COMPANY_TRACKER, { initiativeId: initiative._id }), '_blank');
    } catch (e) {
      generateErrorToast(e);
    } finally {
      toggle();
    }
  };

  const handleClose = () => {
    setView(IntegrationModalView.Info);
    toggle();
  };

  const renderView = () => {
    switch (view) {
      case IntegrationModalView.Info:
        return (
          <InfoView
            initiative={initiative}
            isLoading={isLoading}
            handleBack={toggle}
            handleNext={() => setView(IntegrationModalView.Currency)}
          />
        );
      case IntegrationModalView.Currency:
        return (
          <CurrencyView
            initiative={initiative}
            handleBack={() => setView(IntegrationModalView.Info)}
            handleSubmit={handleSubmit}
            currency={currency}
            setCurrency={setCurrency}
            confirmed={confirmed}
            setConfirmed={setConfirmed}
          />
        );
    }
  };

  return (
    <>
      <IntegrationCard variant={variant} toggle={handleClose} />
      <Modal isOpen={isOpen} toggle={handleClose} backdrop='static' returnFocusAfterClose={false}>
        <ModalHeader toggle={handleClose}>Connect Company Tracker</ModalHeader>
        {renderView()}
      </Modal>
    </>
  );
};
